// @ts-strict-ignore
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Avatar, DropdownButton, Spacer, Typography } from "@aviary";
import { AppleSSOButton } from "@shared/components/LoginOptions/AppleSSOButton/AppleSSOButton";
import { GoogleSSOButton } from "@shared/components/LoginOptions/GoogleSSOButton/GoogleSSOButton";
import { hasErrors } from "@shared/helpers/hasErrors";
import { logGaSignupEvent } from "@shared/helpers/logGaSignupEvent";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { DesignationDropdown } from "@unauthenticated/shared/components/DesignationDropdown/DesignationDropdown";
import { DesignationTooltip } from "@unauthenticated/shared/components/DesignationTooltip/DesignationTooltip";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { ContinueWithButtons } from "@unauthenticated/shared/components/PractitionerSignUp/ContinueWithButtons/ContinueWithButtons";
import { ImAPatientButton } from "@unauthenticated/shared/components/PractitionerSignUp/ImAPatientButton/ImAPatientButton";
import type { SubmitValues } from "@unauthenticated/shared/components/PractitionerSignUpForm/PractitionerSignUpForm";
import { PractitionerSignUpForm } from "@unauthenticated/shared/components/PractitionerSignUpForm/PractitionerSignUpForm";
import { SignUpCarouselOrFooter } from "@unauthenticated/shared/components/SignUpCarouselOrFooter/SignUpCarouselOrFooter";
import { SignUpTitle } from "@unauthenticated/shared/components/SignUpTitle/SignUpTitle";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { usePractitionerSignUpUnauthenticatedSharedMutation as usePractitionerSignUpMutation } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation.generated";
import type { PractitionerSignUpUnauthenticatedSharedMutation as PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation.generated";
import type { PractitionerType } from "@unauthenticated/shared/data/types/PractitionerType";
import { usePractitionerType } from "@unauthenticated/shared/hooks/usePractitionerType";
import { l } from "@unauthenticated/shared/locales/i18n";

import * as styles from "./PractitionerSignUp.styles";

interface Props {
  practitionerTypes: PractitionerType[];
  additionalAttributes?: PractitionerAdditionalAttributesType;
  pageVersion: "prac_by_default_with_sso" | "prac_by_default" | "oauth_prac";
  onCompleted: (data: PractitionerSignUpMutationData) => void;
  isOAuth?: boolean;
  ssoErrors: string;
  onSignInRedirect?: () => void;
}

const PractitionerSignUp = ({
  practitionerTypes,
  additionalAttributes,
  pageVersion,
  onCompleted,
  isOAuth,
  ssoErrors,
  onSignInRedirect,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const pracType = usePractitionerType();
  const signUpPage = isOAuth ? "oauth_sign_up_page" : "practitioner_sign_up_page";
  const isPeerReferral = !!additionalAttributes?.peerReferralDisplayAttributes;

  const designationFromURL = practitionerTypes?.find(
    practitionerType => practitionerType.code === pracType
  );

  const [isFormVisible, setIsFormVisible] = useState(!!pracType || isPeerReferral);
  const [designationId, setDesignationId] = useState(designationFromURL?.id || "");

  const practitioner = practitionerTypes.find(
    practitionerType => practitionerType.id === designationId
  );

  const [signUp, { loading, data }] = usePractitionerSignUpMutation({
    onCompleted: payload => {
      if (!hasErrors(payload?.auth?.practitionerSignUp?.errors)) {
        const { id, storeId } = payload?.auth?.practitionerSignUp?.practitioner || {};
        logGaSignupEvent(id, storeId);
      }
      onCompleted(payload);
    },
  });

  const handleSubmit = (values: SubmitValues) => {
    const { gToken, ...attributes } = values;

    const {
      peerReferralDisplayAttributes: unusedPeerReferralDisplayAttribute,
      ...restAdditionalAttributes
    } = additionalAttributes;

    signUp({
      variables: {
        input: {
          attributes: { ...attributes, optional: restAdditionalAttributes },
          captchaToken: gToken,
        },
      },
    });
  };

  const getTriggerText = () => {
    const designation = practitionerTypes.find(type => type.id === designationId);

    return designation?.description || t(l.common.IAmA);
  };

  const renderContinueWithButtonsOrForm = () => {
    if (isFormVisible) {
      return (
        <>
          <div css={styles.fullWidth}>
            <GoogleSSOButton
              pracTypeId={practitioner?.id}
              css={styles.continueWithButton}
              disabled={!!isPeerReferral && !designationId}
              pageVersion={pageVersion}
              signUpPage={signUpPage}
              additionalAttributes={additionalAttributes}
            />
            <AppleSSOButton
              pracTypeId={practitioner?.id}
              css={styles.continueWithButton}
              isOrTextRendered
              disabled={!!isPeerReferral && !designationId}
              pageVersion={pageVersion}
              signUpPage={signUpPage}
              additionalAttributes={additionalAttributes}
            />
          </div>

          <PractitionerSignUpForm
            isPeerReferral={isPeerReferral}
            onSubmit={handleSubmit}
            errors={data?.auth?.practitionerSignUp?.errors?.fields}
            isLoading={loading}
            pageVersion={pageVersion}
            requireDesignation
            designationId={designationId}
            setDesignationId={setDesignationId}
            signUpPage={signUpPage}
            onSignInRedirect={onSignInRedirect}
            initialEmail={additionalAttributes?.peerReferralDisplayAttributes?.referredEmail ?? ""}
            initialFirstName={
              additionalAttributes?.peerReferralDisplayAttributes?.referredFirstName ?? ""
            }
            initialLastName={
              additionalAttributes?.peerReferralDisplayAttributes?.referredLastName ?? ""
            }
          />
        </>
      );
    }

    return (
      <ContinueWithButtons
        pracTypeId={practitioner?.id}
        setIsFormVisible={setIsFormVisible}
        isActive={!!designationId}
        pageVersion={pageVersion}
        additionalAttributes={additionalAttributes}
        isOAuth
      />
    );
  };

  const renderPeerReferralTitle = () => {
    if (isPeerReferral) {
      const { referrerFirstName, referrerAvatarUrl } =
        additionalAttributes?.peerReferralDisplayAttributes;

      return (
        <div css={styles.peerReferralTitle}>
          {!!referrerAvatarUrl && (
            <Avatar
              size="small"
              image={referrerAvatarUrl}
              css={styles.userAvatar}
              label={`${referrerFirstName} avatar`}
            />
          )}
          <Typography type="h3">
            {t(l.practitionerSignUp.peerReferral.signUpTitle, { referrerFirstName })}
          </Typography>
        </div>
      );
    }
  };

  const renderTitleSection = () => {
    if (isPeerReferral) return null;

    return (
      <>
        {!isOAuth && <ImAPatientButton />}
        <SignUpTitle user={t(l.practitionerSignUp.Practitioner)} />
      </>
    );
  };

  const renderFooter = () => {
    if (isPeerReferral) {
      return null;
    }

    return (
      <SignUpCarouselOrFooter
        isWithinViewport={tablet.greaterThan}
        onSignInRedirect={onSignInRedirect}
      />
    );
  };

  return (
    <>
      {renderPeerReferralTitle()}
      <FormWrapper data-e2e="prac_by_default">
        <div css={styles.textAlign}>
          {renderTitleSection()}

          <Typography css={styles.subTitle}>
            <Trans i18nKey={l.practitionerSignUp.LetsGetStarted}>
              Let's get you started! All accounts are 100%{" "}
              <strong css={styles.freeText}>free</strong>
            </Trans>
          </Typography>

          {!isFormVisible && <Spacer height="doubleHalf" />}

          <SSOErrorBox ssoErrors={ssoErrors} onRedirect={onSignInRedirect} />

          <div css={styles.designationRow}>
            <DesignationDropdown practitionerTypes={practitionerTypes} setValue={setDesignationId}>
              <DropdownButton isFullWidth isCentered data-e2e="practitioner-type-trigger">
                {getTriggerText()}
              </DropdownButton>
            </DesignationDropdown>
            <DesignationTooltip />
          </div>

          {renderContinueWithButtonsOrForm()}
        </div>

        <div css={[styles.signUpFooterSection, styles.textAlign]}>{renderFooter()}</div>
      </FormWrapper>
    </>
  );
};

export { PractitionerSignUp };
